/*
 * <BeProgressBar> -- React wrapper for the .be-progress-bar CSS component: the
 * BeOS R5 determinate progress bar (recreates BeOS's BStatusBar). Reconstructed
 * from rdef_src/libbe_r5/reconstructed/BStatusBar.Draw.match.cpp; see
 * components/progress-bar.css for provenance. (Indeterminate = <BeBarberPole>.)
 *
 *   <BeProgressBar label="Copying…" value={60} max={100} trailing="60%" />
 */

import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeProgressBarProps = BeProps<
	"div",
	{
		/** Current value. */
		value: number;
		/** Maximum value (default 100). */
		max?: number;
		/** Left-aligned label above the trough. */
		label?: string;
		/** Right-aligned trailing text above the trough (e.g. a percentage). */
		trailing?: string;
		/** Override the BarColor() fill (default the Be blue #7378b8). */
		barColor?: string;
		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
	}
>;

export const BeProgressBar = beComponent<BeProgressBarProps, HTMLDivElement>("BeProgressBar", (props, be) => {
	const { value, max = 100, label, trailing, barColor, scale, fluid, className, style, ...rest } = props;

	const pct = Math.max(0, Math.min(100, (value / max) * 100));
	// R5 FillBar: split = left + (cur/max)*Width() with BeOS Width() = right-left
	// (one less than the column count) and the fill spanning left..split
	// INCLUSIVE; cur == 0 paints no fill at all (BStatusBar.Draw.match.cpp).
	const fillStyle: React.CSSProperties = {
		width: pct > 0 ? `calc(round(nearest, (100% - var(--be-px)) * ${pct / 100}, var(--be-px)) + var(--be-px))` : "0",
	};
	if (barColor) {
		fillStyle.background = barColor;
		// Auto-shift the raised bevel from the custom colour. shift_color() ==
		// tint_color() maps onto color-mix (lite=shift(c,0.59), dark=shift(c,1.407);
		// see segmented-progress-bar.css / Notes/23). The default azure keeps its
		// byte-verified --be-progress-fill-lite/-dark tokens (color-mix rounds where
		// BeOS floors), so this only kicks in for an explicit barColor.
		const s = fillStyle as Record<string, string>;
		s["--be-progress-fill-lite"] = `color-mix(in srgb, ${barColor} 59%, white)`;
		s["--be-progress-fill-dark"] = `color-mix(in srgb, ${barColor} 59.3%, black)`;
	}

	return (
		<div
			role="progressbar"
			aria-valuenow={value}
			aria-valuemin={0}
			aria-valuemax={max}
			aria-label={label}
			{...rest}
			{...be("be-progress-bar", fluid && "is-fluid")}
		>
			{(label != null || trailing != null) && (
				<div className="be-progress-text">
					{pixelText(label, "be-progress-text__label")}
					<span className="be-progress-text__trailing">{pixelText(trailing)}</span>
				</div>
			)}
			<div className="be-progress-bar__trough">
				<div className="be-progress-bar__fill" style={fillStyle} />
			</div>
		</div>
	);
});
